import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation } from 'react-router-dom'
import { ArrowDownToLine, ArrowLeftRight, ArrowUpFromLine, CheckCircle2, TrendingUp, XCircle } from 'lucide-react'
import { useTRPC } from '@/lib/trpc'
import type { RouterOutputs } from '@/lib/api-types'
import { formatRawTokenAmount, formatTokenAmount } from '@/lib/format'
import { noteOwnActivity, useInvalidatePortfolio } from '@/lib/portfolio'
import { SOL_MINT, USDC_MINT, USDT_MINT, tokenLogo } from '@/lib/default-tokens'
import { PlatformMark } from './platform-chip'
import { Ticker } from './ticker'

type Flow = RouterOutputs['progress']['list']['flows'][number]
type TitlePart = Flow['title'][number]
type Target = Flow['target']

// taller, with the estimate's fill along the bottom edge (John, Nov 1 2026)
const CHIP = 'relative flex h-11 shrink-0 items-center gap-2 overflow-hidden rounded-lg border border-line bg-surface-2 px-3.5'
const CORE_BY_SYMBOL: Record<string, string> = { SOL: SOL_MINT, USDC: USDC_MINT, USDT: USDT_MINT }
/** how long a finished chip stays (John, Nov 1 2026: "let it hang in there for 3 seconds when done") */
const FINISHED_MS = 3_200
/** a thin muted divider between the parts of a chip (John, Nov 1 2026: a muted bar, not bullet points) */
const Sep = () => (
  <span className="mx-0.5 text-muted" aria-hidden="true">
    |
  </span>
)

/** the words of a chip as the server lists them: text, a token with its logo and amount, an amount, a place's mark (John, Nov 1 2026: "CEX logos without the word"), a divider */
function Title({ parts }: { parts: TitlePart[] }) {
  return (
    <>
      {parts.map((part, i) => {
        switch (part.type) {
          case 'text':
            return <span key={i}>{part.text}</span>
          case 'sep':
            return <Sep key={i} />
          case 'place':
            return <PlatformMark key={i} id={part.place} size="size-3.5" />
          case 'amount':
            return (
              <span key={i}>
                {formatRawTokenAmount(part.amountRaw, part.decimals, part.symbol)} {part.symbol}
              </span>
            )
          case 'token': {
            const amount =
              part.amountRaw && part.decimals !== null ? formatRawTokenAmount(part.amountRaw, part.decimals, part.symbol) : part.amountUi !== null ? formatTokenAmount(part.amountUi, part.symbol) : null
            return (
              <span key={i} className="flex items-center gap-1">
                {amount ? <span>{amount}</span> : null}
                {/* a core coin's logo by its mint; anything else brings its own image */}
                <Ticker symbol={part.symbol} imageUrl={tokenLogo(part.address ?? CORE_BY_SYMBOL[part.symbol], part.imageUrl ?? undefined)} />
              </span>
            )
          }
        }
      })}
    </>
  )
}

/** where a chip opens: the dialog that follows the flow; a strategy opens its detail on the page the bar is on (John, Nov 2 2026) */
function hrefOf(target: Target, pathname: string) {
  switch (target.kind) {
    case 'deposit':
      return `/dashboard?action=deposit&id=${target.id}`
    case 'withdrawal':
      return `/dashboard?action=withdraw&withdrawal=${target.id}`
    case 'privateWithdrawal':
      return `/dashboard?action=withdraw&private=${target.id}`
    case 'transfer':
      return `/dashboard?action=withdraw&transfer=${target.id}`
    case 'position':
      return `/dashboard?action=swap&position=${target.id}&mode=${target.mode}`
    case 'swap':
      return `/dashboard?action=swap&swap=${target.id}`
    case 'strategy':
      return pathname === '/dashboard' ? `/dashboard?strategy=${target.id}` : `/strategies?id=${target.id}`
  }
}

function KindIcon({ kind }: { kind: Flow['kind'] }) {
  const Icon = kind === 'deposit' ? ArrowDownToLine : kind === 'withdraw' ? ArrowUpFromLine : kind === 'strategy' ? TrendingUp : ArrowLeftRight
  return <Icon className="size-3.5 text-muted" aria-hidden="true" />
}

/**
 * The queue of money in flight, docked at the bottom like a browser's
 * download bar (John, Oct 31 2026): deposits arriving, withdrawals paying
 * out, swaps, buys, sells and strategy orders running. It shows only while
 * something is moving; a chip opens its flow's progress; whatever leaves
 * the queue stays a few seconds more as a finished chip, with a check or a
 * cross and its bar draining (John, Nov 1 2026: "remove the separated
 * snackbars; let it hang in there for 3 seconds when done").
 *
 * The server says what is moving (src/api/progress.ts, one read with no
 * caps and every rule in one place with its tests); this bar only draws it.
 * The activity feed reads it again the moment a record changes, so the poll
 * is the backstop (John, Nov 1 2026: "make everything snappier and not poll
 * based").
 */
export function ActivityBar() {
  const { pathname } = useLocation()
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const invalidatePortfolio = useInvalidatePortfolio()
  const [busy, setBusy] = useState(false)
  const progress = useQuery({ ...trpc.progress.list.queryOptions(), refetchInterval: busy ? 3_000 : 15_000, refetchOnWindowFocus: true })
  const flows = useMemo(() => progress.data?.flows ?? [], [progress.data])

  // the balance watcher stays quiet about what is moving here
  useEffect(() => {
    noteOwnActivity(flows.flatMap((flow) => flow.symbols))
    setBusy(flows.length > 0)
  }, [flows])

  // whatever left the queue since last time stays a few seconds as a finished chip, with its record's own verdict
  const [finished, setFinished] = useState<Array<{ id: string; title: TitlePart[]; outcome: 'ok' | 'failed' }>>([])
  const finish = (id: string, title: TitlePart[], outcome: 'ok' | 'failed') => {
    setFinished((list) => [...list.filter((f) => f.id !== id), { id, title, outcome }])
    window.setTimeout(() => setFinished((list) => list.filter((f) => f.id !== id)), FINISHED_MS)
  }
  const seen = useRef<Map<string, Flow>>(new Map())
  const firstSeen = useRef<Map<string, number>>(new Map())
  const shownFill = useRef<Map<string, number>>(new Map())
  useEffect(() => {
    // a read that failed keeps the last flows on screen; only a read that answered can say a flow is gone
    if (!progress.data) return
    const now = new Map(flows.map((flow) => [flow.id, flow]))
    for (const [id, flow] of seen.current) {
      if (now.has(id)) continue
      firstSeen.current.delete(id)
      shownFill.current.delete(id)
      // the record says how it ended; a flow that merely left the list (a custody switch) ends nothing
      void queryClient
        .fetchQuery({ ...trpc.progress.outcome.queryOptions({ id }), staleTime: 0 })
        .then((result) => {
          if (result.outcome === 'ok') {
            // a deposit's title names the token that actually arrived, not what the chip guessed (Nov 1 2026)
            finish(id, result.title ?? flow.title, 'ok')
            noteOwnActivity(flow.symbols)
            void invalidatePortfolio()
          } else if (result.outcome === 'failed') finish(id, flow.title, 'failed')
        })
        .catch(() => undefined)
    }
    seen.current = now
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flows])

  // one clock for every chip's fill; a chip without an estimate fills from when it was first seen
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (flows.length === 0) return
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [flows.length])

  if (flows.length === 0 && finished.length === 0) return null
  return (
    <div className="shrink-0 border-t border-line bg-bg px-4 py-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] sm:px-6 md:pb-2 lg:px-8" role="status" aria-live="polite">
      <div className="mx-auto flex w-full max-w-6xl items-center gap-3 overflow-x-auto text-sm">
        {flows.map((flow) => {
          // the estimate's share so far, held just short of full until the flow really ends; without an estimate the bar
          // fills over a minute from when the chip appeared
          let started = flow.startedAt ? Date.parse(flow.startedAt) : (firstSeen.current.get(flow.id) ?? NaN)
          if (Number.isNaN(started)) {
            started = now
            firstSeen.current.set(flow.id, now)
          }
          const byTime = Math.min(0.95, Math.max(0.02, (now - started) / 1000 / (flow.etaSeconds ?? 60)))
          // a flow with stages sits where its dialog's rail sits and only creeps inside that band
          const banded = flow.band ? Math.min(flow.band.ceiling, Math.max(flow.band.floor, byTime)) : byTime
          // and never moves backwards (John, Nov 1 2026: "never the back-and-forth flash"): a step read lower holds its place
          const fill = Math.max(banded, shownFill.current.get(flow.id) ?? 0)
          shownFill.current.set(flow.id, fill)
          return (
            // the whole chip is the link (John, Oct 31 2026); the title alone shows, the stage is its tooltip (John, Nov 1 2026:
            // "I'll click on it if I want the further status")
            <Link key={flow.id} to={hrefOf(flow.target, pathname)} title={flow.stage} className={`${CHIP} transition-colors hover:border-accent/60 hover:bg-surface-3`}>
              <span className="absolute inset-x-0 bottom-0 h-0.5 bg-surface-3" aria-hidden="true">
                <span className="block h-full bg-accent transition-[width] duration-1000 ease-linear" style={{ width: `${fill * 100}%` }} />
              </span>
              <KindIcon kind={flow.kind} />
              <span className="flex items-center gap-1 whitespace-nowrap font-medium">
                <Title parts={flow.title} />
              </span>
            </Link>
          )
        })}
        {finished.map((f) => (
          <span key={`finished-${f.id}`} className={`${CHIP} ${f.outcome === 'ok' ? 'border-accent/50' : 'border-negative/50'}`}>
            <Drain ok={f.outcome === 'ok'} />
            {f.outcome === 'ok' ? <CheckCircle2 className="size-4 shrink-0 text-accent" aria-hidden="true" /> : <XCircle className="size-4 shrink-0 text-negative" aria-hidden="true" />}
            <span className="flex items-center gap-1 whitespace-nowrap font-medium">
              <Title parts={f.title} />
            </span>
          </span>
        ))}
      </div>
    </div>
  )
}

/** the finished chip's bar: full, then draining over the three seconds the chip stays */
function Drain({ ok }: { ok: boolean }) {
  const [started, setStarted] = useState(false)
  useEffect(() => {
    const id = window.setTimeout(() => setStarted(true), 30)
    return () => window.clearTimeout(id)
  }, [])
  return (
    <span className="absolute inset-x-0 bottom-0 h-0.5 bg-surface-3" aria-hidden="true">
      <span className={`block h-full ${ok ? 'bg-accent' : 'bg-negative'} transition-[width] duration-[3000ms] ease-linear`} style={{ width: started ? '0%' : '100%' }} />
    </span>
  )
}
